import { useMemo } from "react";

import { css } from "@compiled/react";

import { DEFAULT_LOCALE, useLocale } from "@/libraries/web-framework";
import { TextComponent } from "@/templates/agile-hub/types";

export type RichTextComponentProps = Omit<TextComponent, "__typename">;

// NOTE: Nested selectors are necessary here to be able to style the dangerouslySetInnerHTML blob
const richTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        fontSize: "1.125rem",
        lineHeight: "1.55555556",
        marginBlockStart: "0"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "table": {
        borderCollapse: "collapse",
        borderSpacing: "0",
        border: "0",
        emptyCells: "hide",
        display: "block"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "thead": {
        border: "0",
        display: "table-header-group"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "th": {
        padding: "8px",
        textAlign: "left"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "tbody": {
        display: "table-row-group"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "tr": {
        border: "0",
        display: "table-row"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "td": {
        textAlign: "left",
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "#dfe1e5",
        borderStyle: "solid",
        borderWidth: "1px",
        padding: "16px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".table-wrapper": {
        display: "block",
        overflowX: "auto",
        marginBlockEnd: "64px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "table p": {
        marginBlockStart: "0",
        marginBlockEnd: "0"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a": {
        "color": "#0052cc",
        "textDecoration": "underline",
        "cursor": "pointer",
        "&:hover": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#0065ff",
            textDecoration: "none"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "ol": {
        "list-style": "none !important",
        "counter-reset": "orderedList",
        "position": "relative",
        "padding-inline-start": "40px !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["ol li:before"]: {
        "border": "2px solid #ebecf0",
        "border-radius": "2pc",
        "content": "counter(orderedList)",
        "counter-increment": "orderedList",
        "height": "1.75pc",
        "margin-inline-end": "14px",
        "line-height": "1.75",
        "width": "1.75pc",
        "inset-inline-start": "0",
        "position": "absolute",
        "margin-block-start": "-2px",
        "text-align": "center",
        "font-size": "1rem"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "blockquote": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#42526e",
        fontSize: "1.5rem",
        lineHeight: "1.5",
        margin: "0 0 24px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "blockquote:before": {
        content: "open-quote"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "blockquote:after": {
        content: "close-quote"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "blockquote > p": {
        display: "inline",
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#42526e",
        fontSize: "1.5rem",
        lineHeight: "1.5"
    }
});

export const RichTextComponent = (data: RichTextComponentProps) => {
    const localeCode = useLocale();
    const html = useMemo(() => {
        let initialHtml = data.text || "";

        // Hacky stuff: Need to wrap tables in a div to make them scrollable
        const tableRegex = /<table[^>]*>[\s\S]*?<\/table>/g;
        const tableMatch = initialHtml.match(tableRegex);
        if (tableMatch) {
            const tableHtml = tableMatch[0];
            const newTableHtml = `<div class="table-wrapper">${tableHtml}</div>`;
            initialHtml = initialHtml.replace(tableHtml, newTableHtml);
        }

        // Hacky stuff pt2: Need to fix relative localized links
        const relativeLinkRegex = /(?:\"https:\/\/\/)(?<urlSlug>.+?)\"/g;
        initialHtml = initialHtml.replace(
            relativeLinkRegex,
            (_match, _p1, _offset, _string, groups) => {
                return localeCode === DEFAULT_LOCALE
                    ? `"/${groups.urlSlug}"`
                    : `"/${localeCode}/${groups.urlSlug}"`;
            }
        );

        // handle case where link is relative and not localized
        const relativeUrlRegex = new RegExp(
            `href="\\/(${localeCode}\\/)?([^"]*)"`,
            "g"
        );
        initialHtml = initialHtml.replace(
            relativeUrlRegex,
            (_match, _p1, _p2) => {
                // If the locale code is already present, return the match as is
                if (_p1) return _match;
                // Otherwise, prepend the locale code to the URL
                return `href="/${localeCode}/${_p2}"`;
            }
        );

        // handle case where absolute WAC links are not localized
        const absolutePathRegex =
            /\"https:\/\/www\.atlassian\.com\/(?![a-z]{2}\/)(?<urlSlug>.+?)\"/g;
        initialHtml = initialHtml.replace(
            absolutePathRegex,
            (_match, _p1, _offset, _string, groups) => {
                return localeCode === DEFAULT_LOCALE
                    ? `"/${groups.urlSlug}"`
                    : `"/${localeCode}/${groups.urlSlug}"`;
            }
        );

        // Hacky stuff pt3: replace &amp;nbsp; with &nbsp;
        initialHtml = initialHtml
            .replace(/&amp;nbsp;/g, "&nbsp;")
            .replace(/&amp;amp;/g, "&amp;");

        return initialHtml;
    }, [data.text, localeCode]);

    return (
        <div css={[richTextCss]} dangerouslySetInnerHTML={{ __html: html }} />
    );
};
